// even though we're lazy loading the babylonjs modules, purely type imports are removed at runtime,
// so no impact on bundle size :) https://www.typescriptlang.org/docs/handbook/release-notes/typescript-3-8.html
import type {Engine, Scene} from '@babylonjs/core';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components';
import * as React from 'react';
import {useEffect, useRef} from 'react';

import {envIsIntegration} from '../config';
import {useGetTrackingCallbacks} from './PanelMediaBrowser/MediaBrowserPanelTileTrackingContext';
import {Media3DProps} from './PointCloud';
import {InstrumentedCenteredWaveLoader} from './utility/InstrumentedLoader';

// Copied from BabylonJS docs https://doc.babylonjs.com/features/featuresDeepDive/babylonViewer/react/
interface HTML3DElementAttributes
  extends React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLElement>,
    HTMLElement
  > {
  source?: string;
  environment?: string;
}

declare module 'react' {
  interface JSX {
    IntrinsicElements: {
      'babylon-viewer': HTML3DElementAttributes;
    };
  }
}

export const Viewer3D: React.FunctionComponent<Media3DProps> = props => {
  const {
    url,
    width,
    height,
    controls,
    backgroundColor,
    tileMedia,
    tileMediaSpec,
    getMediaTracking,
  } = props;

  const canvasRef = useRef(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);

  const handleFullscreen = async () => {
    if (containerRef.current) {
      try {
        await containerRef.current.requestFullscreen();
      } catch (e) {
        console.error('Failed to enter fullscreen:', e);
      }
    }
  };

  const {trackSuccess, trackError} = useGetTrackingCallbacks(
    tileMedia,
    tileMediaSpec,
    getMediaTracking
  );

  useEffect(() => {
    if (!canvasRef.current) {
      return undefined;
    }

    let engine: Engine;
    let scene: Scene;
    let cleanup: (() => void) | undefined;

    const initScene = async () => {
      setIsLoading(true);
      try {
        // Load only the specific modules we need
        const {Vector3} = await import('@babylonjs/core/Maths/math.vector');
        const {ArcRotateCamera} = await import(
          '@babylonjs/core/Cameras/arcRotateCamera'
        );
        const {HemisphericLight} = await import(
          '@babylonjs/core/Lights/hemisphericLight'
        );
        const {SceneLoader} = await import(
          '@babylonjs/core/Loading/sceneLoader'
        );

        const {registerBuiltInLoaders} = await import(
          '@babylonjs/loaders/dynamic'
        );
        registerBuiltInLoaders();

        const {Engine} = await import('@babylonjs/core/Engines/engine');
        const {Scene} = await import('@babylonjs/core/scene');
        const {Color4} = await import('@babylonjs/core/Maths/math.color');

        engine = new Engine(canvasRef.current, true);
        scene = new Scene(engine);
        if (backgroundColor) {
          scene.clearColor = new Color4(
            backgroundColor.r / 255,
            backgroundColor.g / 255,
            backgroundColor.b / 255,
            backgroundColor.a
          );
        }

        const camera = new ArcRotateCamera(
          'camera',
          0,
          Math.PI / 3,
          10,
          Vector3.Zero(),
          scene
        );
        camera.attachControl(canvasRef.current, true);
        camera.wheelPrecision = 100;
        camera.pinchPrecision = 100;
        camera.upperRadiusLimit = 1000;
        camera.lowerRadiusLimit = 1;
        // don't auto rotate on tests because that'll mess up integration test screenshots
        camera.useAutoRotationBehavior =
          !envIsIntegration &&
          window.location.hostname !== 'host.docker.internal'; // if you're running local tests, you'll have to run it in the docker container for it to work because the env is set to 'local';
        if (camera.autoRotationBehavior) {
          camera.autoRotationBehavior.idleRotationSpeed = 0.5;
          camera.autoRotationBehavior.idleRotationWaitTime = 2000;
          camera.autoRotationBehavior.zoomStopsAnimation = false;
        }

        const light = new HemisphericLight(
          'light',
          new Vector3(0, 1, 0),
          scene
        );
        light.intensity = 0.7;

        await new Promise<void>(resolve => {
          SceneLoader.ImportMesh('', url, '', scene, function (meshes) {
            camera.zoomOn(meshes);
            // Only once the scene is loaded and the meshes are zoomed in on should we consider
            // that the 3D viewer is loaded and ready for screenshotting
            setIsLoading(false);
            trackSuccess?.();
            resolve();
          });
        });

        engine.runRenderLoop(() => {
          scene.render();
        });

        const handleResize = () => {
          engine.resize();
        };

        const handleFullscreenChange = () => {
          setIsFullscreen(document.fullscreenElement === containerRef.current);
          engine.resize();
        };

        window.addEventListener('resize', handleResize);
        document.addEventListener('fullscreenchange', handleFullscreenChange);

        cleanup = () => {
          window.removeEventListener('resize', handleResize);
          document.removeEventListener(
            'fullscreenchange',
            handleFullscreenChange
          );
          engine.dispose();
        };
      } catch (error) {
        console.error('Error initializing Babylon.js:', error);
        trackError?.();
        setIsLoading(false);
      }
    };

    initScene();
    return cleanup;
  }, [url, backgroundColor, trackSuccess, trackError]);

  return (
    <div
      ref={containerRef}
      className={`night-aware relative ${
        isFullscreen ? 'h-screen w-screen' : 'inline-block'
      }`}>
      <canvas
        data-test={`${
          canvasRef.current != null && !isLoading ? 'loaded' : 'loading'
        }-canvas`}
        ref={canvasRef}
        className={isFullscreen ? 'h-full w-full' : ''}
        style={!isFullscreen ? {width, height} : undefined}
      />
      {isLoading && (
        <InstrumentedCenteredWaveLoader name="3d-viewer" size="small" />
      )}
      {controls && (
        <div className="absolute right-2 top-2 z-10">
          <Button
            onClick={handleFullscreen}
            icon="full-screen-mode-expand"
            className="hover:text-white"
            variant="ghost"
          />
        </div>
      )}
    </div>
  );
};

export default Viewer3D;
